import { createListCollection, Field as ChakraField, Fieldset, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { InputNumber } from '@components/ui/input/InputNumber';
import MultiSelect from '@components/ui/multiSelect/MultiSelect';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { CustomSelect } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectProfilesWithoutRestricted } from '@redux/authent/authent.selectors';
import { GlobalScreenConfigurationSelectors, PlatformSelectors } from '@redux/config/config.selectors';
import { RootState } from '@redux/store';
import { haveSameUniqueElements } from '@utils/common.utils';
import { Field, FormikProps, FormikProvider } from 'formik';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { NormalizedGroup } from '@/types/authent/groups.types';
import { RoleEnum } from '@/types/authent/roles.types';

type Props = {
  formik: FormikProps<Partial<NormalizedGroup>>;
  isRestricted: boolean;
};

export default function GroupForm({ formik, isRestricted }: Readonly<Props>) {
  const profiles = useAppSelector(selectProfilesWithoutRestricted);
  const globalScreenConfigurations = useAppSelector(
    GlobalScreenConfigurationSelectors.selectAllGlobalScreenConfigurations,
  );
  const canAccessGlobalConfiguration = useAppSelector((state) =>
    hasRole(state, RoleEnum.ACCESS_GLOBAL_SCREEN_CONFIGURATION),
  );
  const canAccessPlatform = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_PLATFORM));
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const canAccessProfile = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_PROFILE));

  const collection = createListCollection({
    items: canAccessProfile
      ? profiles.map((profile) => ({
          label: profile.name,
          value: profile.id.toString(),
        }))
      : formik.values.profile && formik.values.profileName
        ? [{ value: formik.values.profile, label: formik.values.profileName }]
        : [],
  });

  const defaultPlatformOptions = createListCollection({
    items: platforms
      .filter((p) => formik.values.platforms?.includes(p.code))
      .map((platform) => ({
        label: platform.name,
        value: platform.code,
      })),
  });

  useEffect(() => {
    if (!!formik.values.defaultPlatform && !formik.values.platforms?.includes(formik.values.defaultPlatform)) {
      void formik.setFieldValue('defaultPlatform', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.platforms]);

  return (
    <CockpitFormLayout title="components.usersManagement.groups.general.title">
      <FormikProvider value={formik}>
        <Fieldset.Root
          invalid={Object.keys(formik.errors).some((key) => formik.touched[key as keyof typeof formik.touched])}
        >
          <VStack gap={5}>
            <ChakraField.Root variant="cockpit" required invalid={formik.touched.name && !!formik.errors.name}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.usersManagement.groups.name" />
                <RequiredIndicator />
                {formik.initialValues.name !== formik.values.name && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="name" id="name" disabled={isRestricted} />
            </ChakraField.Root>
            <ChakraField.Root variant="cockpit" invalid={formik.touched.ldapGroupDn && !!formik.errors.ldapGroupDn}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.usersManagement.groups.ldapGroupDn" />
                <RequiredIndicator />
                {formik.initialValues.ldapGroupDn !== formik.values.ldapGroupDn && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="ldapGroupDn" id="ldapGroupDn" disabled={isRestricted} />
            </ChakraField.Root>
            {canAccessPlatform && !isRestricted && (
              <>
                <ChakraField.Root variant="cockpit">
                  <ChakraField.Label width="100%" height="20px">
                    <FormattedMessage id="components.usersManagement.groups.platforms" />
                    <RequiredIndicator />
                    {!haveSameUniqueElements(formik.initialValues.platforms!, formik.values.platforms!) && (
                      <ModifiedTag />
                    )}
                  </ChakraField.Label>
                  <MultiSelect
                    values={formik.values.platforms ?? []}
                    label="components.usersManagement.groups.addPlatforms"
                    collection={createListCollection({
                      items: platforms.map((platform) => ({
                        label: platform.name,
                        value: platform.code,
                      })),
                    })}
                    onChange={(values) => formik.setFieldValue('platforms', values)}
                  />
                </ChakraField.Root>
                <ChakraField.Root
                  variant="cockpit"
                  invalid={formik.touched.defaultPlatform && !!formik.errors.defaultPlatform}
                >
                  <ChakraField.Label width="100%" height="20px">
                    <FormattedMessage id="components.usersManagement.groups.defaultPlatform" />
                    {formik.initialValues.defaultPlatform !== formik.values.defaultPlatform && <ModifiedTag />}
                  </ChakraField.Label>
                  <CustomSelect
                    name="defaultPlatform"
                    value={formik.values.defaultPlatform ?? undefined}
                    collection={defaultPlatformOptions}
                    clearable
                    onChange={(value) => formik.setFieldValue('defaultPlatform', value)}
                  />
                </ChakraField.Root>
              </>
            )}
            {canAccessGlobalConfiguration && (
              <ChakraField.Root
                variant="cockpit"
                required
                invalid={formik.touched.globalScreenConfig && !!formik.errors.globalScreenConfig}
              >
                <ChakraField.Label width="100%" height="20px">
                  <FormattedMessage id="components.usersManagement.groups.general.configuration" />
                  {formik.initialValues.globalScreenConfig !== formik.values.globalScreenConfig && <ModifiedTag />}
                </ChakraField.Label>
                <CustomSelect
                  value={formik.values.globalScreenConfig ?? undefined}
                  collection={createListCollection({
                    items: globalScreenConfigurations.map((config) => ({
                      label: config.name,
                      value: config.code,
                    })),
                  })}
                  clearable
                  onChange={(value) => formik.setFieldValue('globalScreenConfig', value)}
                />
              </ChakraField.Root>
            )}
            {!isRestricted && (
              <ChakraField.Root variant="cockpit" required invalid={formik.touched.profile && !!formik.errors.profile}>
                <ChakraField.Label width="100%" height="20px">
                  <FormattedMessage id="components.usersManagement.groups.profile" />
                  {formik.initialValues.profile !== formik.values.profile && <ModifiedTag />}
                </ChakraField.Label>
                <CustomSelect
                  value={formik.values.profile?.toString() ?? undefined}
                  collection={collection}
                  clearable
                  disabled={!canAccessProfile}
                  onChange={(value) => {
                    const profile = profiles.find((profile) => profile.id.toString() === value);
                    formik.setFieldValue('profile', profile?.id ?? null);
                    formik.setFieldValue('profileName', profile?.name ?? null);
                  }}
                />
              </ChakraField.Root>
            )}
            <VStack padding={4} gap={1} width="100%" border="2px solid" borderColor="neutral.850" alignItems="start">
              <HStack width="100%">
                <Text>
                  <FormattedMessage id="components.usersManagement.groups.general.inactivity" />
                </Text>
                <Switch
                  size="sm"
                  marginLeft="auto"
                  checked={formik.values.inactivityTime !== null}
                  onCheckedChange={() => {
                    formik.setFieldValue('inactivityTime', formik.values.inactivityTime === null ? 10 : null);
                  }}
                />
              </HStack>
              {formik.values.inactivityTime !== null && (
                <>
                  <Text color="neutral.300">
                    <FormattedMessage id="components.usersManagement.groups.general.inactivityDescription" />
                  </Text>
                  <ChakraField.Root
                    variant="cockpit"
                    required
                    invalid={formik.touched.inactivityTime && !!formik.errors.inactivityTime}
                  >
                    <Field
                      as={InputNumber}
                      type="text"
                      name="inactivityTime"
                      id="inactivityTime"
                      allowMouseWheel
                      onChange={(value?: number) => formik.setFieldValue('inactivityTime', value)}
                      suffix="min"
                    />
                  </ChakraField.Root>
                </>
              )}
            </VStack>
          </VStack>
        </Fieldset.Root>
      </FormikProvider>
    </CockpitFormLayout>
  );
}
